import { useEffect, useEffectEvent, useState, type RefObject } from 'react';
import type { ISiteMapPoint } from '@interfaces/ISiteMap';
import type { SiteMapMode } from './useEquipmentPlacement';
import { clampToPlan, distance, getUnitsPerPx } from './siteMap.logic';

/** A click within this many SCREEN pixels of the first vertex closes the
 *  polygon (spec §2), same as Enter with ≥3 points. Screen-based, not a
 *  fixed plan-unit distance: a real blueprint's viewBox can be thousands of
 *  units wide, where a plan-unit threshold shrinks to sub-pixel and becomes
 *  unhittable. Converted to plan units per click via `getUnitsPerPx`. */
const CLOSE_THRESHOLD_PX = 12;
const MIN_DRAW_POINTS = 3;

interface IUseAreaDrawingArgs {
  mode: SiteMapMode;
  setMode: (mode: SiteMapMode) => void;
  /** The interactive overlay `<svg>` — read from event handlers for the
   *  live plan-units-per-screen-px scale, never during render. */
  overlayRef: RefObject<SVGSVGElement | null>;
}

/**
 * Draw-an-area tool: the in-progress polygon's vertices, live cursor, and
 * the close/cancel choreography — extracted from `SiteMap` the same way
 * `useEquipmentPlacement` holds the place-equipment tool. `SiteMap` keeps
 * the create mutation itself (it owns the data hooks); this owns everything
 * up to the New Area dialog opening.
 */
export function useAreaDrawing({ mode, setMode, overlayRef }: IUseAreaDrawingArgs) {
  const [draftPoints, setDraftPoints] = useState<ISiteMapPoint[]>([]);
  const [cursor, setCursor] = useState<ISiteMapPoint | null>(null);
  // Plan-units-per-screen-px, for sizing the draft vertex squares at a fixed
  // on-screen size — refreshed from event handlers (never read from
  // `overlayRef` during render; that trips `react-hooks/refs`). jsdom never
  // implements `getScreenCTM`, so this stays at `getUnitsPerPx`'s own
  // `DEFAULT_UNITS_PER_PX` fallback in tests that don't mock it — harmless,
  // they only assert vertex count, not size.
  const [unitsPerPx, setUnitsPerPx] = useState(1);
  const [newAreaOpen, setNewAreaOpen] = useState(false);

  const start = () => {
    setMode('draw');
    setDraftPoints([]);
    setCursor(null);
  };

  /** Cancels the in-progress polygon and closes the new-area dialog if it was
   *  open — used by Esc, the toolbar's toggle-off click, the dialog's own
   *  Cancel, and a landed create. */
  const resetDraw = () => {
    setMode('view');
    setDraftPoints([]);
    setCursor(null);
    setNewAreaOpen(false);
  };

  /** Finishes the in-progress polygon by opening the New Area dialog — the
   *  one close path shared by the stage-click threshold (a click within
   *  `CLOSE_THRESHOLD_PX` of the first vertex) and a direct click on the
   *  first-vertex handle itself (`SiteMap`'s draft layer). */
  const closePolygon = () => {
    setNewAreaOpen(true);
  };

  // Window-level so Enter/Esc work regardless of DOM focus, active only while
  // drawing. `draftPoints` is READ (not a dep) via an Effect Event, which
  // always sees the latest render — so the listener re-subscribes only on a
  // real draw-mode entry/exit, not on every vertex added.
  const onDrawKeyDown = useEffectEvent((e: KeyboardEvent) => {
    if (e.key === 'Escape') resetDraw();
    else if (e.key === 'Enter' && draftPoints.length >= MIN_DRAW_POINTS) setNewAreaOpen(true);
  });
  useEffect(() => {
    if (mode !== 'draw') return;
    const onKeyDown = (e: KeyboardEvent) => onDrawKeyDown(e);
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mode]);

  const handleClick = (rawPoint: ISiteMapPoint) => {
    // Read synchronously rather than off `unitsPerPx` state — a click isn't
    // guaranteed a preceding mousemove (real fast clicks, and every draw test
    // fires clicks without one) to have already refreshed that state, and
    // the close check below needs THIS click's scale, not a stale one.
    const currentUnitsPerPx = getUnitsPerPx(overlayRef.current);
    setUnitsPerPx(currentUnitsPerPx);
    // Clamped to the backend's own >=0 bound — a click outside the
    // blueprint's viewBox can otherwise hand back a negative plan coordinate
    // that 400s on create.
    const p = clampToPlan(rawPoint);
    const first = draftPoints[0];
    if (
      first &&
      draftPoints.length >= MIN_DRAW_POINTS &&
      distance(p, first) <= CLOSE_THRESHOLD_PX * currentUnitsPerPx
    ) {
      closePolygon();
      return;
    }
    setDraftPoints((pts) => [...pts, p]);
  };

  const handleMove = (p: ISiteMapPoint) => {
    setCursor(p);
    setUnitsPerPx(getUnitsPerPx(overlayRef.current));
  };

  // Once ≥3 points are down, the first vertex is the close target.
  const canClose = draftPoints.length >= MIN_DRAW_POINTS;

  return { draftPoints, cursor, unitsPerPx, canClose, newAreaOpen, start, resetDraw, closePolygon, handleClick, handleMove };
}
